import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Package,
  TrendingUp,
  ShoppingCart,
  Tag,
  Users,
  BarChart3,
  Settings,
  LogOut,
  Menu,
  X
} from 'lucide-react';
import auth from '../utils/auth';
import { Button } from './ui/button';

export const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const user = auth.getCurrentUser();

  const menuItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/products', icon: Package, label: 'Products' },
    { path: '/stock', icon: TrendingUp, label: 'Stock Management' },
    { path: '/sales', icon: ShoppingCart, label: 'Sales / POS' },
    { path: '/categories', icon: Tag, label: 'Categories' },
    { path: '/suppliers', icon: Users, label: 'Suppliers' },
    { path: '/reports', icon: BarChart3, label: 'Reports' },
    { path: '/settings', icon: Settings, label: 'Settings' },
  ];

  const handleLogout = () => {
    auth.logout();
    navigate('/');
  };

  return (
    <div className="flex h-screen bg-muted">
      {/* Sidebar */}
      <aside
        className={`sidebar-transition ${sidebarOpen ? 'w-64' : 'w-0 md:w-20'
          } bg-slate-900 text-white flex-shrink-0 overflow-hidden`}
      >
        <div className="h-full flex flex-col">
          {/* Logo */}
          <div className="p-6 border-b border-slate-800">
            <h1 className="font-heading text-2xl font-black tracking-tight">
              {sidebarOpen ? 'RetailFlow' : 'RF'}
            </h1>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-2">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`flex items-center gap-3 px-4 py-3 rounded-sm font-medium transition-colors ${isActive
                      ? 'bg-primary text-white'
                      : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                    }`}
                >
                  <Icon size={20} />
                  {sidebarOpen && <span>{item.label}</span>}
                </Link>
              );
            })}
          </nav>

          {/* User info */}
          <div className="p-4 border-t border-slate-800">
            {sidebarOpen ? (
              <div className="mb-3">
                <p className="text-sm font-medium">{user?.username}</p>
                <p className="text-xs text-slate-400 uppercase">{user?.role}</p>
              </div>
            ) : null}
            <Button
              onClick={handleLogout}
              data-testid="logout-button"
              variant="ghost"
              className="w-full justify-start text-slate-300 hover:bg-slate-800 hover:text-white"
            >
              <LogOut size={20} />
              {sidebarOpen && <span className="ml-3">Logout</span>}
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="bg-white border-b border-border p-4 flex items-center justify-between">
          <Button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            variant="ghost"
            size="icon"
            data-testid="toggle-sidebar"
          >
            {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
          </Button>

          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-sm font-semibold">{user?.username}</p>
              <p className="text-xs text-muted-foreground uppercase tracking-wide">{user?.role}</p>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto bg-muted p-6 md:p-8">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;